import React, {useState} from 'react';
import {ActionButton, Link, mergeStyles, PrimaryButton, Stack, Text, useTheme} from '@fluentui/react';
import {toast} from 'react-toastify';
import {
    useGetStorefrontProductSuggestionsQuery,
    useListBundleOnStorefrontMutation,
} from '../../store/endpoints/StorefrontSettingsEndpoints';
import {IStorefrontSuggestedBundle} from '../../models/Storefront';
import useCan, {STOREFRONT_ADMIN} from '../../permissions/ability';
import CurrencyDisplay from '../Currency/CurrencyDisplay';
import StorefrontBundleModal from '../ProductBundles/StorefrontBundleModal';
import SuggestedBundlesPanel from './SuggestedBundlesPanel';
import SuggestionReasonChip from './SuggestionReasonChip';
import {useFittedCardCount} from './useFittedCardCount';
import {
    bundleAvailability,
    bundleDetailLine,
    bundleSuggestionReason,
    BUNDLE_STRIP_SIZE,
    IBundleAvailability,
} from './suggestedBundlesLogic';
import {addErrorMessage, suggestionAmountState} from './suggestedProductsLogic';

const ADD_FAILED = 'We could not add that bundle to the store.';
const ADD_MANY_FAILED = 'We could not add those bundles to the store.';

/** Card geometry, in px so the fit arithmetic is exact against a measured pixel width. */
const CARD_WIDTH = 260;
const CARD_GAP = 12;

interface ISuggestedBundlesStripProps {
    storefront: string;
    /** Bundles were listed. The bundles grid is untagged, so the host refetches it itself. */
    onBundlesAdded: () => void;
}

/** The dot color for an availability state: green sellable, red out of stock, amber unpriceable. */
const availabilityDotColor = (kind: IBundleAvailability['kind'], palette: {green: string, red: string, orange: string}): string =>
    kind === 'inStock' ? palette.green : kind === 'unpriced' ? palette.orange : palette.red;

/**
 * One compact card in the strip: what the bundle is, what it would cost, and a single click that shelves it.
 *
 * Deliberately the same card as a suggested product's, because the two strips sit one tab apart and a bundle
 * is being weighed the same way. Only the cost and the Add button carry any weight; the reason chip and the
 * stock state repeat down the row, so they are said quietly.
 *
 * No markup control anywhere on this card or in the panel behind it: a bundle prices itself from its own
 * pricing rules rather than inheriting a per-listing markup, so there is nothing here to override.
 */
const BundleSuggestionCard: React.FC<{
    bundle: IStorefrontSuggestedBundle;
    pending: boolean;
    disabled: boolean;
    onAdd: () => void;
    onOpenBundle: () => void;
}> = ({bundle, pending, disabled, onAdd, onOpenBundle}) => {
    const theme = useTheme();
    const detail = bundleDetailLine(bundle);
    const availability = bundleAvailability(bundle);
    // A bundle is never priced by quote the way a product can be, so there is no hidden state to carry.
    const costState = suggestionAmountState(bundle.cost, undefined);
    // A class rather than inline style so the hover lift is a real CSS selector instead of mouse-event state.
    const cardClass = mergeStyles({
        width: CARD_WIDTH,
        flexShrink: 0,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${theme.palette.neutralLighter}`,
        background: theme.palette.white,
        transition: 'box-shadow .15s ease, border-color .15s ease',
        selectors: {
            ':hover': {
                boxShadow: theme.effects.elevation8,
                borderColor: theme.palette.neutralTertiaryAlt,
            },
        },
    });
    return (
        <Stack className={cardClass} tokens={{childrenGap: 6}}>
            <Stack horizontal verticalAlign='center' tokens={{childrenGap: '0.5em'}}>
                {/* Dropped entirely rather than replaced with a placeholder tile when the bundle has no image,
                  * because a row of identical grey squares says nothing and the text is better off with it. */}
                {bundle.imageUrl && (
                    <img
                        src={bundle.imageUrl}
                        alt=''
                        onClick={onOpenBundle}
                        style={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            objectFit: 'contain',
                            borderRadius: 4,
                            cursor: 'pointer',
                        }}
                    />
                )}
                <Stack grow styles={{root: {minWidth: 0}}}>
                    <Link
                        onClick={onOpenBundle}
                        title={bundle.name}
                        styles={{root: {
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                        }}}
                    >
                        {bundle.name ?? 'Untitled'}
                    </Link>
                    {detail && (
                        <Text
                            variant='small'
                            block
                            nowrap
                            title={detail}
                            styles={{root: {
                                color: theme.palette.neutralSecondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}}
                        >
                            {detail}
                        </Text>
                    )}
                </Stack>
            </Stack>

            <Stack horizontal verticalAlign='center' horizontalAlign='space-between' tokens={{childrenGap: '0.5em'}}>
                <Stack.Item styles={{root: {minWidth: 0}}}>
                    <SuggestionReasonChip reason={bundleSuggestionReason(bundle)}/>
                </Stack.Item>
                {/* A dot and a word rather than a filled badge: availability is context here, not the
                  * headline. Amber is the third state: not out of stock, but unpriceable for this store, with
                  * the failing part numbers on hover. */}
                <Stack
                    horizontal
                    verticalAlign='center'
                    tokens={{childrenGap: 4}}
                    title={availability.tooltip}
                    styles={{root: {flexShrink: 0, whiteSpace: 'nowrap'}}}
                >
                    <span
                        style={{
                            width: 8,
                            height: 8,
                            flexShrink: 0,
                            borderRadius: '50%',
                            background: availabilityDotColor(availability.kind, theme.palette),
                        }}
                    />
                    <Text variant='small' nowrap styles={{root: {color: theme.palette.neutralSecondary}}}>
                        {availability.label}
                    </Text>
                </Stack>
            </Stack>

            <Stack horizontal verticalAlign='center' horizontalAlign='space-between' tokens={{childrenGap: '0.5em'}}>
                {costState === 'value' ? (
                    <Stack
                        horizontal
                        verticalAlign='baseline'
                        tokens={{childrenGap: 4}}
                        styles={{root: {minWidth: 0}}}
                        title='What this bundle costs your organization'
                    >
                        <Text styles={{root: {
                            fontSize: 16,
                            fontWeight: 600,
                            color: theme.palette.neutralPrimary,
                        }}}>
                            <CurrencyDisplay value={bundle.cost}/>
                        </Text>
                        <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>cost</Text>
                    </Stack>
                ) : (
                    <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>No cost</Text>
                )}
                <PrimaryButton
                    text={pending ? 'Adding...' : 'Add'}
                    title={`Add ${bundle.name ?? 'this bundle'} to the store`}
                    disabled={disabled || pending}
                    onClick={onAdd}
                    styles={{root: {height: 28, minWidth: 0, padding: '0 14px', borderRadius: 4}}}
                />
            </Stack>
        </Stack>
    );
};

/**
 * "Your other stores already sell these bundles, and this one does not."
 *
 * The bundle twin of the suggested products strip, above the bundles grid: what the organization's other
 * storefronts already carry first, then whatever active bundles are simply sitting in the catalog unlisted.
 * It renders nothing at all when there is nothing to draw on, when the read fails, or when everything it
 * would suggest is already on this store, so the tab is unchanged for anyone it has nothing to offer.
 *
 * It owns the three pieces of state both surfaces share: what has already been added, what is in flight, and
 * the one bundle editor modal. The panel behind "See all" runs its own read but defers all of that to here,
 * so adding something in the panel also takes the card out of the strip underneath it.
 */
const SuggestedBundlesStrip: React.FC<ISuggestedBundlesStripProps> = ({storefront, onBundlesAdded}) => {
    // UX gate only, and a real one here: without this permission the suggestions read itself is refused, so
    // the strip would only produce a 403. Hoisted rather than called inline in JSX because CAN uses
    // useSelector internally, and calling it under a branch would break hook order.
    const canStorefrontAdmin = useCan(STOREFRONT_ADMIN);
    const [addedIds, setAddedIds] = useState<string[]>([]);
    const [pendingId, setPendingId] = useState<string | undefined>();
    const [addingMany, setAddingMany] = useState<boolean>(false);
    const [panelOpen, setPanelOpen] = useState<boolean>(false);
    const [openBundleId, setOpenBundleId] = useState<string | undefined>();
    const [listBundle] = useListBundleOnStorefrontMutation();
    // How wide the row actually is decides how many cards there are, so the strip never scrolls sideways.
    const {rowRef, count: cardCount} = useFittedCardCount(CARD_WIDTH, CARD_GAP);

    const suggestions = useGetStorefrontProductSuggestionsQuery(
        // size 0 asks the API to run no product passes at all: this strip only reads bundles.
        {storefront, size: 0, bundleSize: BUNDLE_STRIP_SIZE},
        // Refetch on every mount: the added-card bookkeeping is local state, so serving the cached read after
        // a navigate-away-and-back would resurrect cards for bundles that are already on the store.
        {skip: !storefront || !canStorefrontAdmin, refetchOnMountOrArgChange: true}
    );

    const rememberAdded = (ids: string[]) => {
        setAddedIds((current) => {
            const fresh = ids.filter((id) => !current.includes(id));
            return fresh.length > 0 ? [...current, ...fresh] : current;
        });
    };

    /**
     * Put one bundle on this storefront, straight off its card.
     *
     * A single in-flight id at a time keeps a double click from sending the same bundle twice, though the
     * route is idempotent either way.
     */
    const addBundle = async (bundle: IStorefrontSuggestedBundle) => {
        if (pendingId || addingMany || addedIds.includes(bundle.id)) return;
        const label = bundle.name ?? 'this bundle';
        setPendingId(bundle.id);
        const toastId = toast.loading(`Adding ${label} to the store...`);
        try {
            const result = await listBundle({storefront, bundleId: bundle.id}).unwrap();
            // A business refusal arrives as a 200 carrying success false, so the flag is what decides here
            // rather than the HTTP status.
            if (result?.success === false) throw new Error(result.message || ADD_FAILED);
            toast.update(toastId, {
                type: 'success',
                render: `Added ${label} to the store`,
                isLoading: false,
                autoClose: 3000,
            });
            rememberAdded([bundle.id]);
            onBundlesAdded();
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: addErrorMessage(error, ADD_FAILED),
                isLoading: false,
                autoClose: 4000,
            });
        }
        setPendingId(undefined);
    };

    /**
     * Put everything the panel has ticked on this storefront, one call each.
     *
     * There is no bulk bundle route, so this is a loop rather than a batch. It runs the calls in order and
     * keeps going past a failure: the bundles that can land should land, and the ones that could not are
     * reported rather than rounded up to success. Resolves with the ids that actually landed so the panel can
     * clear exactly those rows.
     */
    const addBundles = async (bundleIds: string[]): Promise<string[]> => {
        if (bundleIds.length === 0 || pendingId || addingMany) return [];
        setAddingMany(true);
        const noun = (count: number) => `${count} bundle${count === 1 ? '' : 's'}`;
        const toastId = toast.loading(`Adding ${noun(bundleIds.length)} to the store...`);
        const landed: string[] = [];
        let firstFailure: string | undefined;
        for (const bundleId of bundleIds) {
            try {
                const result = await listBundle({storefront, bundleId}).unwrap();
                if (result?.success === false) throw new Error(result.message || ADD_MANY_FAILED);
                landed.push(bundleId);
            } catch (error) {
                if (firstFailure === undefined) firstFailure = addErrorMessage(error, ADD_MANY_FAILED);
            }
        }
        if (landed.length === 0) {
            toast.update(toastId, {
                type: 'error',
                render: firstFailure ?? ADD_MANY_FAILED,
                isLoading: false,
                autoClose: 4000,
            });
        } else if (landed.length < bundleIds.length) {
            toast.update(toastId, {
                type: 'warning',
                render: `Added ${landed.length} of ${noun(bundleIds.length)}. First failure: ${firstFailure ?? ADD_MANY_FAILED}`,
                isLoading: false,
                autoClose: 6000,
            });
        } else {
            toast.update(toastId, {
                type: 'success',
                render: `Added ${noun(landed.length)} to the store`,
                isLoading: false,
                autoClose: 3000,
            });
        }
        if (landed.length > 0) {
            rememberAdded(landed);
            onBundlesAdded();
        }
        setAddingMany(false);
        return landed;
    };

    const bundles = suggestions.data?.bundles ?? [];
    const visible = bundles.filter((b) => !addedIds.includes(b.id));

    // Nothing to say, so say nothing: no spinner, no empty state, no shift in the tab's layout.
    if (!canStorefrontAdmin) return null;
    if (suggestions.isLoading || suggestions.isError || bundles.length === 0) return null;

    return (
        <Stack tokens={{childrenGap: '0.5em'}} styles={{root: {marginBottom: '1em'}}}>
            {/* No subtitle: the cards each say why they are here, and one blanket reason for a mixed list would
              * be wrong for most of them. "See all" lives up here rather than as a tile at the end of the row,
              * where it would be the first thing pushed off the edge. */}
            <Stack horizontal verticalAlign='center' horizontalAlign='space-between' tokens={{childrenGap: '0.5em'}}>
                <Text variant='mediumPlus' styles={{root: {fontWeight: 600}}}>Suggested Bundles</Text>
                <ActionButton
                    text='See all'
                    iconProps={{iconName: 'ChevronRight'}}
                    title='See all suggested bundles'
                    onClick={() => setPanelOpen(true)}
                    styles={{root: {height: 28, padding: 0, flexShrink: 0}}}
                />
            </Stack>

            {/* One row, only as many cards as fit. The hidden overflow is a safety net for the frame between a
              * resize and the remeasure, not the way anything is meant to be reached. */}
            <div ref={rowRef} style={{overflow: 'hidden'}}>
                <Stack horizontal verticalAlign='stretch' tokens={{childrenGap: CARD_GAP}}>
                    {visible.slice(0, cardCount).map((bundle) => (
                        <BundleSuggestionCard
                            key={bundle.id}
                            bundle={bundle}
                            pending={pendingId === bundle.id}
                            // One add already running holds the rest, so a burst of clicks cannot queue up.
                            disabled={addingMany || (!!pendingId && pendingId !== bundle.id)}
                            onAdd={() => addBundle(bundle)}
                            onOpenBundle={() => setOpenBundleId(bundle.id)}
                        />
                    ))}
                </Stack>
            </div>

            {panelOpen && (
                <SuggestedBundlesPanel
                    storefront={storefront}
                    addedIds={addedIds}
                    busy={addingMany || !!pendingId}
                    onOpenBundle={setOpenBundleId}
                    onAddSelected={addBundles}
                    onDismiss={() => setPanelOpen(false)}
                />
            )}

            {openBundleId && (
                <StorefrontBundleModal
                    isOpen={true}
                    bundleId={openBundleId}
                    onDismiss={() => setOpenBundleId(undefined)}
                />
            )}
        </Stack>
    );
};

export default SuggestedBundlesStrip;
